import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export type ColorScheme = 'light' | 'dark' | 'system';

export interface HeaderButtons {
  translation: boolean;
  activity: boolean;
  notifications: boolean;
  search: boolean;
  userMenu: boolean;
  pageActions: boolean;
}

export const APPEARANCE_CHANGED_EVENT = 'code-factory:appearance:changed';

export interface AppearanceValues {
  colorScheme: ColorScheme;
  themeName: string;
  accentColor: string;
  topbarColor: string;
  shellColor: string;
  globalRadius: string;
  fieldRadius: string;
  buttonRadius: string;
  cardRadius: string;
  formSpacing: string;
  tableDensity: string;
  fontSizeScale: string;
  buttonAppearance: string;
  formActionsPosition: string;
  formSectionStyle: string;
  sidebarDefaultState: string;
  mobileNavMode: string;
  shellLayout: string;
  confirmDestructive: boolean | null;
  toastPosition: string;
}

const DEFAULT_HEADER_BUTTONS: HeaderButtons = {
  translation: true,
  activity: true,
  notifications: true,
  search: true,
  userMenu: true,
  pageActions: true,
};

const SYSTEM_DEFAULTS: AppearanceValues = {
  colorScheme: 'system',
  themeName: '',
  accentColor: '',
  topbarColor: '',
  shellColor: '',
  globalRadius: '',
  fieldRadius: '',
  buttonRadius: '',
  cardRadius: '',
  formSpacing: '',
  tableDensity: '',
  fontSizeScale: '',
  buttonAppearance: '',
  formActionsPosition: '',
  formSectionStyle: '',
  sidebarDefaultState: '',
  mobileNavMode: '',
  shellLayout: '',
  confirmDestructive: null,
  toastPosition: '',
};

interface AppearanceStore {
  defaults: AppearanceValues;
  // Layer 2 — platform admin defaults (no backend; persisted locally)
  adminSettings: Partial<AppearanceValues>;
  // Layer 3 — per-user overrides saved in personal preferences
  userPreferences: Partial<AppearanceValues>;
  // Layer 4 — live preview while editing (never persisted)
  previewUi: Partial<AppearanceValues> | null;

  headerButtons: HeaderButtons;

  setAdminSettings: (values: Partial<AppearanceValues>) => void;
  setUserPreferences: (values: Partial<AppearanceValues>) => void;
  setPreviewUi: (v: Partial<AppearanceValues> | null) => void;
  setHeaderButtons: (v: Partial<HeaderButtons>) => void;
  resetToDefaults: () => void;
}

export function selectEffective(s: AppearanceStore): AppearanceValues {
  const r = <K extends keyof AppearanceValues>(key: K): AppearanceValues[K] =>
    (s.previewUi?.[key] as AppearanceValues[K] | undefined) ??
    (s.userPreferences[key] as AppearanceValues[K] | undefined) ??
    (s.adminSettings[key] as AppearanceValues[K] | undefined) ??
    s.defaults[key];
  return {
    colorScheme: r('colorScheme'),
    themeName: r('themeName'),
    accentColor: r('accentColor'),
    topbarColor: r('topbarColor'),
    shellColor: r('shellColor'),
    globalRadius: r('globalRadius'),
    fieldRadius: r('fieldRadius'),
    buttonRadius: r('buttonRadius'),
    cardRadius: r('cardRadius'),
    formSpacing: r('formSpacing'),
    tableDensity: r('tableDensity'),
    fontSizeScale: r('fontSizeScale'),
    buttonAppearance: r('buttonAppearance'),
    formActionsPosition: r('formActionsPosition'),
    formSectionStyle: r('formSectionStyle'),
    sidebarDefaultState: r('sidebarDefaultState'),
    mobileNavMode: r('mobileNavMode'),
    shellLayout: r('shellLayout'),
    confirmDestructive: r('confirmDestructive'),
    toastPosition: r('toastPosition'),
  };
}

const jsonStorage = createJSONStorage<AppearanceStore>(() => ({
  getItem: (name) => localStorage.getItem(name),
  setItem: (name, value) => {
    localStorage.setItem(name, value);
    window.dispatchEvent(new Event(APPEARANCE_CHANGED_EVENT));
  },
  removeItem: (name) => localStorage.removeItem(name),
}));

export const useAppearanceStore = create<AppearanceStore>()(
  persist(
    (set) => ({
      defaults: SYSTEM_DEFAULTS,
      adminSettings: {},
      userPreferences: {},
      previewUi: null,
      headerButtons: DEFAULT_HEADER_BUTTONS,

      setAdminSettings: (values) =>
        set((s) => ({ adminSettings: { ...s.adminSettings, ...values } })),

      setUserPreferences: (values) =>
        set((s) => ({ userPreferences: { ...s.userPreferences, ...values } })),

      setPreviewUi: (v) => set(() => ({ previewUi: v })),

      setHeaderButtons: (partial) =>
        set((s) => ({ headerButtons: { ...s.headerButtons, ...partial } })),

      resetToDefaults: () => set(() => ({ userPreferences: {} })),
    }),
    {
      name: 'code-factory:appearance:v1',
      storage: jsonStorage,
      // defaults and previewUi are NOT persisted
      partialize: ({ adminSettings, userPreferences, headerButtons }) => ({
        adminSettings,
        userPreferences,
        headerButtons,
      }),
    },
  ),
);
